import { useState, useEffect } from 'react';
import { Plus, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import api from '../api/axios';
import { toast } from 'sonner';
import { format } from 'date-fns';

const HolidayManager = () => {
  const [holidays, setHolidays] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    date: '',
    description: '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchHolidays();
  }, []);

  const fetchHolidays = async () => {
    try {
      const response = await api.get('/holidays');
      setHolidays(response.data);
    } catch (error) {
      console.error('Failed to fetch holidays');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post('/admin/holidays', formData);
      toast.success('Holiday added successfully!');
      setShowForm(false);
      setFormData({ title: '', date: '', description: '' });
      fetchHolidays();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add holiday');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (holidayId) => {
    if (!window.confirm('Are you sure you want to delete this holiday?')) return;

    try {
      await api.delete(`/admin/holidays/${holidayId}`);
      toast.success('Holiday deleted successfully!');
      fetchHolidays();
    } catch (error) {
      toast.error('Failed to delete holiday');
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'MMM d, yyyy');
    } catch {
      return dateString;
    }
  };

  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold font-heading tracking-tight">
          Holiday Calendar Management
        </h2>
        <Button
          data-testid="add-holiday-button"
          onClick={() => setShowForm(!showForm)}
          size="sm"
        >
          {showForm ? (
            <>
              <X className="mr-2 h-4 w-4" />
              Cancel
            </>
          ) : (
            <>
              <Plus className="mr-2 h-4 w-4" />
              Add Holiday
            </>
          )}
        </Button>
      </div>

      {showForm && (
        <div className="mb-6 border border-slate-200 rounded-md p-4 bg-muted/50">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="title">Holiday Name</Label>
                <Input
                  id="title"
                  data-testid="holiday-title-input"
                  placeholder="e.g., Holi, Diwali"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="date">Date</Label>
                <Input
                  id="date"
                  data-testid="holiday-date-input"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description (Optional)</Label>
              <Textarea
                id="description"
                data-testid="holiday-description-input"
                placeholder="Add any notes about this holiday"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
              />
            </div>
            <Button type="submit" data-testid="submit-holiday-button" disabled={loading}>
              {loading ? 'Adding...' : 'Add Holiday'}
            </Button>
          </form>
        </div>
      )}

      <div className="space-y-3" data-testid="holidays-list">
        {holidays.length > 0 ? (
          holidays.map((holiday) => (
            <div
              key={holiday.holiday_id}
              className="flex justify-between items-start border border-slate-200 rounded-md p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <div className="w-16 h-16 bg-accent/10 rounded-md flex flex-col items-center justify-center">
                    <p className="text-xs text-muted-foreground">
                      {format(new Date(holiday.date), 'MMM')}
                    </p>
                    <p className="text-2xl font-bold text-accent">
                      {format(new Date(holiday.date), 'd')}
                    </p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">{holiday.title}</h3>
                    <p className="text-sm text-muted-foreground">{formatDate(holiday.date)}</p>
                    {holiday.description && (
                      <p className="text-sm mt-1">{holiday.description}</p>
                    )}
                  </div>
                </div>
              </div>
              <Button
                data-testid={`delete-holiday-${holiday.holiday_id}`}
                onClick={() => handleDelete(holiday.holiday_id)}
                variant="destructive"
                size="sm"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))
        ) : (
          <div className="text-center py-12">
            <p className="text-muted-foreground">No holidays added yet</p>
            <p className="text-sm text-muted-foreground mt-1">
              Add holidays that will be visible to all employees
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default HolidayManager;